import type { Decimal } from 'decimal.js';
import { BASE_CURRENCY, PERCENT_DP, PRICE_TTL_MS, STALE_MULTIPLIER } from './constants';
import { tryResolveFxRate } from './fx';
import { fromDecimal, roundTo, toDecimal } from './money';
import type { FxRate, Holding, HoldingValuation, Quote } from './types';

export interface ValuateOptions {
  /** เวลาอ้างอิงสำหรับคำนวณอายุราคา — inject ได้เพื่อให้ test deterministic */
  now?: Date;
}

/**
 * ตีมูลค่า holding หนึ่งตัว — spec Section 5.2
 *
 * ⚠️ จุดที่พลาดง่ายที่สุด: `quote.currency` กับ `holding.currency` อาจไม่ตรงกัน
 *    (เช่น ซื้อ BTC บน Bitkub ด้วยบาท แต่ราคาที่ดึงมาเป็น USD)
 *    จึงแปลง "ต้นทุน" กับ "มูลค่าตลาด" เป็นบาท **แยกกันคนละอัตรา** ห้ามใช้อัตราเดียวกัน
 *
 * ทุกอย่างที่คำนวณไม่ได้คืน `null` ไม่ใช่ 0 — เพื่อให้ UI แยกออกระหว่าง
 * "ขาดทุนจนเหลือศูนย์" กับ "ยังไม่รู้ราคา" (spec Section 9.3)
 */
/**
 * ราคาของเงินสดคือ 1 ในสกุลของตัวเองเสมอ จึงสร้างขึ้นมาสด ๆ แทนที่จะไปหาใน cache
 *
 * ทำแบบนี้เพื่อให้สูตรคำนวณทั้งหมดที่เหลือใช้ได้กับเงินสดโดยไม่ต้องแก้อะไรเลย —
 * เงินสดกลายเป็นสินทรัพย์ที่ราคาไม่เคยเปลี่ยนและไม่มีวันเก่า
 */
function cashQuote(holding: Holding, now: Date): Quote {
  return {
    symbol: holding.symbol,
    source: 'cash',
    price: '1',
    currency: holding.currency,
    previousClose: null,
    fetchedAt: now,
  };
}

export function valuateHolding(
  holding: Holding,
  quote: Quote | null,
  rates: ReadonlyMap<string, FxRate>,
  options: ValuateOptions = {},
): HoldingValuation {
  const now = options.now ?? new Date();
  const effectiveQuote = holding.assetType === 'cash' ? cashQuote(holding, now) : quote;

  const quantity = toDecimal(holding.quantity, 'holding.quantity');
  const avgCost = toDecimal(holding.avgCost, 'holding.avgCost');

  // ---- ฝั่งต้นทุน (คำนวณได้เสมอในสกุลเดิม) ----
  const costValue = quantity.mul(avgCost);
  const costFxRate = tryResolveFxRate(holding.currency, BASE_CURRENCY, rates);
  const costValueTHB = costFxRate === null ? null : costValue.mul(costFxRate);

  // ---- ฝั่งมูลค่าตลาด (ต้องมี quote ก่อน) ----
  let marketValue: Decimal | null = null;
  let marketValueTHB: Decimal | null = null;

  if (effectiveQuote !== null) {
    const price = toDecimal(effectiveQuote.price, 'quote.price');
    marketValue = quantity.mul(price);

    const priceFxRate = tryResolveFxRate(effectiveQuote.currency, BASE_CURRENCY, rates);
    marketValueTHB = priceFxRate === null ? null : marketValue.mul(priceFxRate);
  }

  // ---- PnL (ต้องมีครบทั้งสองฝั่งเป็นบาท) ----
  let pnlTHB: Decimal | null = null;
  let pnlPct: string | null = null;

  if (marketValueTHB !== null && costValueTHB !== null) {
    pnlTHB = marketValueTHB.minus(costValueTHB);
    // ต้นทุนศูนย์ (เช่น airdrop) → % ไม่มีความหมาย คืน null ไม่ใช่ Infinity
    if (!costValueTHB.isZero()) {
      pnlPct = roundTo(pnlTHB.div(costValueTHB).mul(100), PERCENT_DP);
    }
  }

  // ---- ความสดของราคา ----
  const quoteAgeMs =
    effectiveQuote === null ? null : now.getTime() - effectiveQuote.fetchedAt.getTime();
  const staleThreshold = PRICE_TTL_MS[holding.assetType] * STALE_MULTIPLIER;
  const isStale = quoteAgeMs === null ? true : quoteAgeMs > staleThreshold;

  return {
    holdingId: holding.id,
    costValue: fromDecimal(costValue),
    costValueTHB: costValueTHB === null ? null : fromDecimal(costValueTHB),
    marketValue: marketValue === null ? null : fromDecimal(marketValue),
    marketValueTHB: marketValueTHB === null ? null : fromDecimal(marketValueTHB),
    pnlTHB: pnlTHB === null ? null : fromDecimal(pnlTHB),
    pnlPct,
    quoteMissing: effectiveQuote === null,
    isStale,
    quoteAgeMs,
  };
}

/**
 * ตีมูลค่าทั้งพอร์ตรวดเดียว
 * `quotes` คีย์ด้วย `holding.priceKey` (spec Section 4.5)
 */
export function valuateHoldings(
  holdings: readonly Holding[],
  quotes: ReadonlyMap<string, Quote>,
  rates: ReadonlyMap<string, FxRate>,
  options: ValuateOptions = {},
): HoldingValuation[] {
  return holdings.map((holding) =>
    valuateHolding(holding, quotes.get(holding.priceKey) ?? null, rates, options),
  );
}
